import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import Select from './Select';

/**
 * Paging for every list table: how many rows a page holds, which page is showing, and the count of
 * what the list holds, drawn above the table and again below it.
 *
 * A list used to arrive whole. A day's invoices came to 47 rows, and the only count was under the
 * last of them, so seeing how many there were meant scrolling past all 47. The count now leads the
 * table as well, and the rows come a page at a time.
 *
 *   const paging = usePagedRows(sortedRows, { id: 'invoices', resetOn: [query, filter, sortConfig] });
 *   <TablePager paging={paging} noun="invoice" />                  // above the table
 *   {paging.pageRows.map(...)}
 *   <TablePager paging={paging} noun="invoice" position="bottom" />
 *
 * A list paged on the server builds the same `paging` object itself (page, pageSize, total,
 * setPage, setPageSize) and passes it in; nothing here needs the rows.
 */

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
export const DEFAULT_PAGE_SIZE = 25;

// The rows-per-page choice is remembered per table, in this browser only. It is a convenience: if
// storage is unavailable the table simply opens at the default.
const sizeKey = (id) => `pamh.pageSize.${id}`;
const readSize = (id, fallback) => {
  if (!id) return fallback;
  try {
    const n = Number(window.localStorage.getItem(sizeKey(id)));
    return PAGE_SIZE_OPTIONS.includes(n) ? n : fallback;
  } catch {
    return fallback;
  }
};
const writeSize = (id, n) => {
  if (!id) return;
  try {
    window.localStorage.setItem(sizeKey(id), String(n));
  } catch {
    /* storage blocked: the choice lasts until the page is left */
  }
};

/**
 * Client-side paging over rows already in memory (filtered and sorted by the caller).
 *
 * `resetOn` lists whatever changes WHICH rows the list holds (a search, a filter, a sort) and sends
 * the list back to its first page. A refresh that brings the same list back with a row more does
 * not: the queue's live updates would otherwise throw the reader off page 3 every few seconds. The
 * page is only ever pulled back far enough to stay inside the list.
 */
export const usePagedRows = (rows, { id, initialSize = DEFAULT_PAGE_SIZE, resetOn = [] } = {}) => {
  const list = Array.isArray(rows) ? rows : [];
  const [pageSize, setPageSizeState] = useState(() => readSize(id, initialSize));
  const [page, setPage] = useState(0);
  const topRef = useRef(null);

  const resetKey = JSON.stringify(resetOn);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setPage(0);
  }, [resetKey]);

  const total = list.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pages - 1);
  const pageRows = useMemo(
    () => list.slice(safePage * pageSize, (safePage + 1) * pageSize),
    [list, safePage, pageSize]
  );

  const setPageSize = (n) => {
    setPageSizeState(n);
    writeSize(id, n);
    setPage(0);
  };

  return { page: safePage, pageSize, total, pages, setPage, setPageSize, pageRows, topRef };
};

const NAV_BUTTON =
  'p-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500';

const plural = (n, noun, nounPlural) => (n === 1 ? noun : nounPlural || `${noun}s`);

/**
 * The bar above or below a paged table. Above, it carries the count and the rows-per-page choice;
 * below, the count again and the page buttons. Both get the previous and next buttons, so a reader
 * at the top of a long page does not have to scroll down to turn it.
 *
 * `filteredFrom` is the size of the list before a search or filter narrowed it, so the count can
 * say "12 of 47". `className` adjusts the padding to the table's own scale. `rounded={false}` is for a
 * bar that does not sit at the corner of its card, under a filter bar or inside a dialog. `toolbar`
 * is a line of its own under the count on the top bar, for a table's own filters or a note on what
 * the count holds.
 */
const TablePager = ({
  paging,
  noun = 'row',
  nounPlural,
  position = 'top',
  filteredFrom,
  busy = false,
  className = 'px-6',
  rounded = true,
  toolbar = null,
}) => {
  if (!paging || !paging.total) return null;
  const { page, pageSize, total, setPage, setPageSize, topRef } = paging;
  const pages = paging.pages || Math.max(1, Math.ceil(total / pageSize));
  const first = page * pageSize + 1;
  const last = Math.min((page + 1) * pageSize, total);
  const paged = pages > 1;
  const isTop = position === 'top';

  // Turning the page from the bottom bar leaves the reader at the foot of the new page, looking at
  // its last rows. Bring the top of the table back into view.
  const goTo = (p) => {
    const next = Math.max(0, Math.min(pages - 1, p));
    if (next === page) return;
    setPage(next);
    const el = topRef?.current;
    if (!isTop && el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'start' });
    }
  };

  const count = paged ? (
    <>
      Showing <span className="font-semibold text-gray-700 tabular-nums">{first}–{last}</span> of{' '}
      <span className="font-semibold text-gray-700 tabular-nums">{total}</span> {plural(total, noun, nounPlural)}
    </>
  ) : (
    <>
      Showing <span className="font-semibold text-gray-700 tabular-nums">{total}</span> {plural(total, noun, nounPlural)}
    </>
  );

  return (
    <div
      ref={isTop ? topRef : undefined}
      className={`${className} py-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-gray-50 text-sm text-gray-500 ${
        isTop ? 'border-b border-gray-200' : 'border-t border-gray-200'
      } ${rounded ? (isTop ? 'rounded-t-2xl' : 'rounded-b-2xl') : ''}`}
      data-testid={`table-pager-${position}`}
    >
      <div aria-live={isTop ? 'polite' : undefined}>
        {count}
        {filteredFrom != null && filteredFrom !== total && (
          <span className="text-gray-400"> (filtered from {filteredFrom})</span>
        )}
      </div>
      {isTop && toolbar && <div className="basis-full order-last">{toolbar}</div>}
      <div className="flex items-center gap-3">
        {isTop && total > PAGE_SIZE_OPTIONS[0] && (
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <Select
              value={pageSize}
              onChange={(v) => setPageSize(Number(v))}
              options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
              className="w-20"
              buttonClassName="px-2 py-1 rounded-lg"
            />
          </div>
        )}
        {paged && (
          <div className="flex items-center gap-1">
            {!isTop && (
              <button type="button" className={NAV_BUTTON} onClick={() => goTo(0)} disabled={page === 0 || busy} title="First page" aria-label="First page">
                <ChevronsLeft className="h-4 w-4" />
              </button>
            )}
            <button type="button" className={NAV_BUTTON} onClick={() => goTo(page - 1)} disabled={page === 0 || busy} title="Previous page" aria-label="Previous page">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-2 whitespace-nowrap">
              Page <span className="font-semibold text-gray-700 tabular-nums">{page + 1}</span> of{' '}
              <span className="font-semibold text-gray-700 tabular-nums">{pages}</span>
            </span>
            <button type="button" className={NAV_BUTTON} onClick={() => goTo(page + 1)} disabled={page >= pages - 1 || busy} title="Next page" aria-label="Next page">
              <ChevronRight className="h-4 w-4" />
            </button>
            {!isTop && (
              <button type="button" className={NAV_BUTTON} onClick={() => goTo(pages - 1)} disabled={page >= pages - 1 || busy} title="Last page" aria-label="Last page">
                <ChevronsRight className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TablePager;
